import { CheckIcon, ChevronLeftIcon, ChevronRightIcon } from '@chakra-ui/icons';
import { Box, Flex, IconButton, Image, useColorMode } from '@chakra-ui/react';
import { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Navigation } from 'swiper';
import 'swiper/css/bundle';
import { Swiper, SwiperSlide } from 'swiper/react';
import {
    LoadingScreen,
    getLocationSelectPagePath,
    getSessionManagementPagePath,
} from '../../App';
import Button from '../../components/button';
import IconCard from '../../components/icon-card';
import Layout from '../../components/layout';
import StepHeader, { FlowFooterNote } from '../../components/step-header';
import Text from '../../components/text';
import { useSession } from '../../hooks/useSession';

const VALUE_PROPS = [
    {
        icon: '/images/icons/shield-icon.svg',
        title: 'Bank-grade',
        description: 'Encryption protects your personal data end to end.',
    },
    {
        icon: '/images/icons/lock-icon.svg',
        title: 'Read-only',
        description: 'We can see your bets — never move money or place wagers.',
    },
];

const BULLETS = [
    'Auto-track every bet across books',
    'See unified P&L and ROI',
    'Disconnect anytime in settings',
];

export default function SecurityMessagePage() {
    const session = useSession();
    const { push, replace } = useHistory();
    const { colorMode, toggleColorMode } = useColorMode();
    const isLight = colorMode === 'light';

    useEffect(() => {
        if (
            session?.uiMode &&
            session?.uiMode != 'system' &&
            session?.uiMode != colorMode
        )
            toggleColorMode();
    }, []);

    // Skip the intro for returning users. The normal link context doesn't include
    // bettorAccounts, so we ask the API whether a Bettor exists for this
    // (user, internalId): 200 => returning (skip), 404 => first-timer (show intro).
    // Gated behind `checking` so returning users never see the intro flash.
    // `replace` keeps the intro out of history so Back doesn't bounce here.
    const [checking, setChecking] = useState(true);

    useEffect(() => {
        if (!session?.cid) return;
        let cancelled = false;

        (async () => {
            // Management session w/ accounts: data already in context, no fetch needed.
            if (
                session?.user?.managementSession &&
                !!session.bettorAccounts?.length
            ) {
                replace(getSessionManagementPagePath(session.cid!));
                return;
            }
            if (!session?.internalId || !session?.token) {
                if (!cancelled) setChecking(false); // missing auth -> just show intro
                return;
            }
            try {
                const res = await fetch(
                    `${process.env.REACT_APP_API_BASE || ''}/v1/bettors/${
                        session.internalId
                    }/bettorAccounts?access=true`,
                    { headers: { Authorization: session.token } }
                );
                if (cancelled) return;
                if (res.status === 200) {
                    // Bettor exists -> returning user; navigate away (leave `checking` true).
                    replace(getLocationSelectPagePath(session.cid!));
                    return;
                }
            } catch (_) {
                /* fall through to showing the intro */
            }
            if (!cancelled) setChecking(false); // 404 / error -> first-timer -> show intro
        })();

        return () => {
            cancelled = true;
        };
    }, [
        session?.cid,
        session?.internalId,
        session?.token,
        session?.bettorAccounts,
        session?.user?.managementSession,
    ]);

    if (checking) return <LoadingScreen />;

    return (
        <Layout
            colorMode={colorMode}
            alert={session?.user?.isSandbox ? 'SANDBOX MODE' : ''}
            stepHeader={<StepHeader step={1} label="Introduction" />}
            footerNote={<FlowFooterNote />}
            buttons={
                <Button
                    onClick={() =>
                        push(getLocationSelectPagePath(session?.cid!))
                    }
                    w="full"
                >
                    Get started
                </Button>
            }
        >
            <Flex
                direction="column"
                align="center"
                gap="24px"
                w="full"
                textAlign="center"
            >
                {session?.logoUrl && (
                    <Image
                        maxH="56px"
                        maxW="200px"
                        objectFit="contain"
                        src={session?.logoUrl}
                        alt={session?.user?.name || ''}
                    />
                )}

                <Flex direction="column" align="center" gap="10px">
                    <Text
                        as="h1"
                        fontSize="28px"
                        fontWeight="700"
                        lineHeight="1.2"
                        maxW="320px"
                    >
                        Link your account in under a minute
                    </Text>
                    <Text size="sm" opacity={0.75} maxW="320px">
                        <Text as="strong">
                            {session?.user?.name || 'This app'}
                        </Text>{' '}
                        uses <Text as="strong">SharpSports</Text> to securely
                        sync your bets. We never store your password.
                    </Text>
                </Flex>

                {/* Value-prop carousel: both cards side-by-side ≥580px, one-at-a-time swipe below */}
                <Box w="full" position="relative" px="34px">
                    <Swiper
                        slidesPerView={2}
                        spaceBetween={16}
                        modules={[Navigation]}
                        navigation={{
                            prevEl: '#valueprop-prev',
                            nextEl: '#valueprop-next',
                        }}
                        loop={false}
                        breakpoints={{
                            0: { slidesPerView: 1, spaceBetween: 12 },
                            580: { slidesPerView: 2, spaceBetween: 16 },
                        }}
                    >
                        {VALUE_PROPS.map((vp) => (
                            <SwiperSlide key={vp.title}>
                                <IconCard
                                    icon={vp.icon}
                                    title={vp.title}
                                    description={vp.description}
                                />
                            </SwiperSlide>
                        ))}
                    </Swiper>
                    <IconButton
                        id="valueprop-prev"
                        aria-label="Previous"
                        icon={<ChevronLeftIcon boxSize="20px" />}
                        position="absolute"
                        top="50%"
                        left="0"
                        transform="translateY(-50%)"
                        zIndex="2"
                        size="sm"
                        w="28px"
                        minW="28px"
                        h="28px"
                        borderRadius="full"
                        variant="ghost"
                        color={isLight ? 'midnightBlue' : 'white'}
                    />
                    <IconButton
                        id="valueprop-next"
                        aria-label="Next"
                        icon={<ChevronRightIcon boxSize="20px" />}
                        position="absolute"
                        top="50%"
                        right="0"
                        transform="translateY(-50%)"
                        zIndex="2"
                        size="sm"
                        w="28px"
                        minW="28px"
                        h="28px"
                        borderRadius="full"
                        variant="ghost"
                        color={isLight ? 'midnightBlue' : 'white'}
                    />
                </Box>

                <Flex
                    direction="column"
                    align="flex-start"
                    gap="12px"
                    w="full"
                    maxW="320px"
                    mb="40px"
                >
                    {BULLETS.map((bullet) => (
                        <Flex key={bullet} align="center" gap="12px">
                            <Flex
                                align="center"
                                justify="center"
                                minW="20px"
                                w="20px"
                                h="20px"
                                borderRadius="full"
                                bg="primary"
                            >
                                <CheckIcon boxSize="10px" color="navy" />
                            </Flex>
                            <Text size="sm" textAlign="left">
                                {bullet}
                            </Text>
                        </Flex>
                    ))}
                </Flex>
            </Flex>
        </Layout>
    );
}
